import { useCallback, useContext, useMemo, useState } from 'react'

import { FileUploader, FixedBanner, Icon } from '@circlefin/components'
import { ErrorType } from '@circlefin/components/lib/FileUploader'
import { useController, y } from '@circlefin/form'
import { FormContext } from '@circlefin/form/Form.Context'
import { IconTooltip } from '@shared/components/common'
import cn from 'classnames'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'

import { useL3Onboarding } from '../../../hooks/l3-onboarding'
import { requiredMessage } from '../../yup/utils'

import { MultipleFileUploaderItem } from './MultipleFileUploaderItem/MultipleFileUploaderItem'

import type {
  FileType,
  FileUploaderProps,
  OnErrorProps,
} from '@circlefin/components/lib/FileUploader'
import type {
  L3OnboardingFlowGroup,
  OnboardingDocument,
  DocumentType,
  CountryCode,
} from '@shared/graphql'

export const filesSchema = y
  .array()
  .of(y.mixed<OnboardingDocument>().required())
  .required()
  .min(1, requiredMessage)

const SFS_MAX_FILE_SIZE = 1024 * 1024 * 10

const DEFAULT_FILE_TYPES: FileType[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

export interface MultipleFileUploaderProps extends FileUploaderProps {
  /**
   * Name of form field.
   */
  name?: string
  /**
   * Tooltip content.
   */
  tooltip?: string | React.ReactNode
  /**
   * All uploaded documents.
   */
  documents: OnboardingDocument[]
  /**
   * Label.
   */
  label?: string
  /**
   * Message.
   */
  message?: React.ReactNode
  /**
   * Document type.
   */
  documentType?: DocumentType
  /**
   * Issued country.
   */
  issuedCountry?: CountryCode
  /**
   * Show upload?
   */
  showUpload?: boolean
  /**
   * Group Name.
   */
  groupName?: L3OnboardingFlowGroup
  /**
   * Accepted file types.
   */
  fileTypes?: FileType[]
  /**
   * Max file size.
   * For now, should just be for Storybook purposes.
   * Since the max should default to SFS_MAX_FILE_SIZE.
   */
  maxSize?: number
  /**
   * Callback after upload.
   */
  onAfterUpload?: () => void
}

export interface UploadProps {
  /**
   * File.
   */
  file: File
}

export interface SelectedFileProps {
  /**
   * Field name.
   */
  fieldName: string
}

export const MultipleFileUploader: React.FC<MultipleFileUploaderProps> = ({
  tooltip,
  name = 'files',
  documents,
  label,
  message,
  className,
  documentType,
  issuedCountry,
  showUpload = true,
  groupName: groupNameProp,
  disabled = false,
  maxSize = SFS_MAX_FILE_SIZE,
  fileTypes = DEFAULT_FILE_TYPES,
  onAfterUpload,
  loading,
  ...props
}) => {
  const [
    {
      onboardingId,
      shouldDisableForm,
      rfiModule,
      uploadDocumentWithoutLinkingResult,
    },
    { uploadDocumentWithoutLinking: uploadDocumentWithoutLinkingMutation },
  ] = useL3Onboarding()
  const { t } = useTranslation('l3Onboarding')

  const { getError } = useContext(FormContext)
  const { field, fieldState } = useController({
    name,
  })
  const [filesUploading, setFilesUploading] = useState(false)
  const [fileError, setFileError] = useState<string>()

  const fieldName = useMemo(() => {
    // handle react hook form index based names
    const nameParts = name.split('.')
    return nameParts[nameParts.length - 1]
  }, [name])

  /**
   * Upload file without linking.
   */
  const uploadDocumentWithoutLinking = useCallback(
    async (
      uploadProps: UploadProps,
    ): Promise<OnboardingDocument | undefined> => {
      if (onboardingId) {
        try {
          const data = await uploadDocumentWithoutLinkingMutation({
            datumName: fieldName,
            file: uploadProps.file,
            documentType,
          })

          if (data) {
            const newFile: OnboardingDocument = {
              documentId:
                data.onboardingUploadDocumentWithoutLinking.documentId,
              fileName: data.onboardingUploadDocumentWithoutLinking.fileName,
              fileSize:
                data.onboardingUploadDocumentWithoutLinking.fileSize.toString(),
              fileType: data.onboardingUploadDocumentWithoutLinking.fileType,
              uploadDate:
                data.onboardingUploadDocumentWithoutLinking.uploadDate,
              documentType,
              issuedCountry,
            }

            return newFile
          }
        } catch {
          // error handled by error boundary
        }
      }
    },
    [
      onboardingId,
      uploadDocumentWithoutLinkingMutation,
      fieldName,
      documentType,
      issuedCountry,
    ],
  )

  /**
   * Validate government issued ID images according to Jumio restrictions.
   */
  const isGovIdImgValid = useCallback(
    async (file: File): Promise<boolean> => {
      return await new Promise((resolve) => {
        const img = new Image()

        img.onload = () => {
          const width = img.width
          const height = img.height
          const isInvalid =
            width < 563 || height < 355 || width > 8000 || height > 8000

          if (isInvalid) {
            setFileError(
              t('forms.common.fileUploadError.imageDimensionsExceeded'),
            )
            resolve(false)
            return
          }

          resolve(true)
        }
        img.src = URL.createObjectURL(file)
      })
    },
    [t],
  )

  /**
   * Determine whether to hide delete button.
   */
  const shouldHideDeleteButton = useCallback(
    (uploadDate?: string) => {
      if (rfiModule?.createDate == null || uploadDate == null) {
        return false
      }
      return new Date(uploadDate) < rfiModule.createDate
    },
    [rfiModule],
  )

  /**
   * Check if file is valid.
   */
  const validateFile = useCallback(
    async (file: File) => {
      const isGovIdImg =
        documentType != null &&
        ['image/jpeg', 'image/png'].includes(file.type) &&
        issuedCountry != null
      const isValid = isGovIdImg ? await isGovIdImgValid(file) : true

      if (!isValid) {
        throw new Error(`Error uploading gov issued ID file ${file.name}`)
      }
    },
    [documentType, isGovIdImgValid, issuedCountry],
  )

  /**
   * Handle multiple files selected without linking.
   */
  const handleFilesSelectedWithoutLinking = useCallback(
    (newFiles: File[]) => {
      const handler = async () => {
        try {
          // TODO: If we are not linking, we don't need to sync the refId. So we don't need to lock the UI with setFilesUploading.
          // But we need to find a way to display the item being uploaded in a loading state: https://circlepay.atlassian.net/browse/ON-2346
          setFilesUploading(true)

          setFileError(undefined)

          let nextDocuments: Array<OnboardingDocument | undefined> = [
            ...documents,
          ]

          // upload other files
          await Promise.all(
            newFiles.map(async (file) => {
              await validateFile(file)

              const uploadedDocument = await uploadDocumentWithoutLinking({
                file,
              })

              if (uploadedDocument == null) {
                return
              }

              onAfterUpload?.()

              nextDocuments = [uploadedDocument, ...nextDocuments]
              field.onChange(nextDocuments)
            }),
          )
        } catch {
          // error handled by error boundary
        } finally {
          setFilesUploading(false)
        }
      }

      void handler()
    },
    [
      documents,
      field,
      onAfterUpload,
      uploadDocumentWithoutLinking,
      validateFile,
    ],
  )

  const handleRemoveFile = useCallback(
    (documentId: string) => {
      const next = documents?.filter((d) => d.documentId !== documentId)
      field.onChange(next)
    },
    [field, documents],
  )

  const clearUploadError = useCallback(() => {
    setFileError(undefined)
    uploadDocumentWithoutLinkingResult?.reset()
  }, [uploadDocumentWithoutLinkingResult])

  /**
   * Handle errors set by FileUploader base component.
   */
  const handleError = useCallback(
    (props?: OnErrorProps) => {
      if (props == null) {
        return
      }

      if (props.type === ErrorType.ERROR_MAX_SIZE) {
        setFileError(
          t(`forms.common.fileUploadError.${props.type}`, {
            // Convert bytes to MB
            maxSize: Math.floor((maxSize ?? 0) / 1000000),
          }),
        )

        return
      }

      if (props.type === ErrorType.ERROR_FILE_TYPE) {
        setFileError(
          t(`forms.common.fileUploadError.${props.type}`, {
            fileTypes: fileTypes
              ?.map((fileType) => {
                const fileTypesTranslation = t(
                  `forms.common.fileUploadError.fileTypes`,
                  {},
                  {
                    returnObjects: true,
                  },
                )
                if (
                  typeof fileTypesTranslation === 'object' &&
                  fileType in fileTypesTranslation
                ) {
                  return fileTypesTranslation[fileType]
                }
              })
              .join(', '),
          }),
        )
      }
    },
    [fileTypes, maxSize, t],
  )

  const errorMessage = useMemo(() => {
    return getError?.(fieldState.error)
  }, [fieldState.error, getError])

  return (
    <fieldset className={cn('flex flex-col gap-y-4', className)}>
      {(label || Boolean(message)) && (
        <>
          <legend className="text-neutral-strong font-circular-bold">
            {label}
          </legend>
          <p className="text-neutral-subtle type-body-sm">{message}</p>
        </>
      )}

      {showUpload && (
        <FileUploader
          data-testid="file-uploader"
          disabled={(shouldDisableForm && !filesUploading) || disabled}
          fileTypes={fileTypes}
          loading={filesUploading}
          maxSize={maxSize}
          name={name}
          onError={handleError}
          onFilesSelected={handleFilesSelectedWithoutLinking}
          {...props}
          allowMultiple
        >
          <Trans
            components={{
              span: (
                <span className="ml-1 text-interactive font-circular-bold" />
              ),
            }}
            i18nKey="forms:fileUploader.multipleFileUploader.dragAndDrop"
          />

          {Boolean(tooltip) && (
            <IconTooltip
              anchorId={`tooltip-${name}`}
              className="z-50"
              content={tooltip}
              delayHide={500}
              iconColor="neutral"
              name="InformationCircleSolid"
            />
          )}
        </FileUploader>
      )}

      {documents != null && documents.length > 0 && (
        <div className="space-y-4">
          {documents
            .toSorted(
              (documentA, documentB) =>
                new Date(documentA.uploadDate).getTime() -
                new Date(documentB.uploadDate).getTime(),
            )
            .map((document, index) => (
              <MultipleFileUploaderItem
                key={index}
                data-testid="file-uploader-item"
                disabled={disabled}
                documentId={document.documentId}
                fileName={document.fileName}
                fileSize={Number(document.fileSize)}
                hideDelete={shouldHideDeleteButton(document?.uploadDate)}
                name={name}
                onRemoveFile={handleRemoveFile}
              />
            ))}
        </div>
      )}

      <FixedBanner
        onDismiss={clearUploadError}
        status="warning"
        visible={
          fileError != null || uploadDocumentWithoutLinkingResult?.error != null
        }
      >
        <FixedBanner.Title>
          {t('forms.common.fileUploadError.title')}
        </FixedBanner.Title>
        <FixedBanner.Description>{fileError}</FixedBanner.Description>
      </FixedBanner>

      {errorMessage != null && (
        <div className="flex flex-row items-center text-error type-body-xs">
          <Icon
            className="mr-1 text-icon-error"
            name="ExclamationCircleSolid"
            size={16}
          />
          {errorMessage}
        </div>
      )}
    </fieldset>
  )
}
